import type { AudioEngine } from "./AudioEngine";
import { OneShotVoices, type OneShotParams } from "./OneShotVoices";
import { startSeamlessLoop } from "./SeamlessLoop";

export type WebAudioEngineConfig = {
  maxConcurrentSources: number;
  /** Told once per one-shot voice stolen over the cap. */
  onVoiceStolen?: () => void;
};

/** The Web Audio engine: the context and its unlock, decoding, the one-shot voices and the seamless loops. */
export class WebAudioEngine implements AudioEngine {
  private ctx: AudioContext | null = null;
  private unlocked = false;
  private readonly voices: OneShotVoices;

  constructor(cfg: WebAudioEngineConfig) {
    this.voices = new OneShotVoices(cfg.maxConcurrentSources, cfg.onVoiceStolen);
  }

  get audioContext(): AudioContext | null {
    return this.ctx;
  }

  isUnlocked(): boolean {
    return this.unlocked;
  }

  ensureContext(): AudioContext {
    if (!this.ctx) this.ctx = new AudioContext();
    return this.ctx;
  }

  async unlockFromUserGesture(): Promise<boolean> {
    const ctx = this.ensureContext();
    try {
      await ctx.resume();
      this.unlocked = ctx.state === "running";
      return this.unlocked;
    } catch {
      this.unlocked = false;
      return false;
    }
  }

  async resumeIfSuspended(): Promise<boolean> {
    const ctx = this.ctx;
    if (!ctx) return false;
    try {
      if (ctx.state === "suspended") await ctx.resume();
      this.unlocked = ctx.state === "running";
      return this.unlocked;
    } catch {
      this.unlocked = false;
      return false;
    }
  }

  decodeFromArrayBuffer(data: ArrayBuffer): Promise<AudioBuffer> {
    return this.ensureContext().decodeAudioData(data.slice(0));
  }

  playOneShot(params: OneShotParams): { stop: () => void } | null {
    const ctx = this.ensureContext();
    if (!this.unlocked) return null;
    return this.voices.play(ctx, params);
  }

  createSeamlessLoop(params: { id: string; buffer: AudioBuffer; destination: AudioNode; overlapSeconds?: number }): { stop: () => void; gainNode: GainNode } | null {
    const ctx = this.ensureContext();
    if (!this.unlocked) return null;
    return startSeamlessLoop(ctx, params);
  }

  notifySlamStop(): void {
    this.voices.stopOnSlam();
  }

  getActiveSourcesCount(): number {
    return this.voices.count;
  }
}
